// Chart colour by job (revamp PR 5, decision #15): the categorical series palette and the status tones.
//
// Categorical (identity): `--lm-series-1..8` + `--lm-series-other` in tokens.css, validated with the dataviz checks.
//   seriesColor(1)                       // 'var(--lm-series-1)', for a Recharts fill / stroke or a ChartConfig colour
//   seriesColor('other')                 // the neutral grey of "Others" and "Unknown"
//   shipmentTypeSlot('Export')           // 2: Import = 1, Export = 2, anything else = 'other', on every page
//   MEASURE_SLOT.goods                   // 2: `processes` = 1, `goods` = 2, on every page
//   topSlices(sortedRows, 5, pick, t('…others'))  // top 5 in slots 1–5, the rest summed as Others ('other')
//   SERIES_BG[3]                         // 'bg-series-3', for a swatch (never text: text stays in the fg tokens)
//
// Status (state, never a series slot; always with its text label):
//   statusColor('ok')                    // 'var(--lm-ok)' for a Recharts fill
//   STATUS_BG.warn                       // 'bg-warn' for a swatch or a meter segment
//
// Rules: a single-series chart and ranked bars use slot 1. A filter that hides a series must not repaint the others:
// assign slots from the full key list, then filter. Past 8 series, fold the tail into Others before colouring.

export const SERIES_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
export type SeriesSlot = (typeof SERIES_SLOTS)[number];
/** A series colour: one of the eight slots, or the neutral `other`. */
export type SeriesColorSlot = SeriesSlot | 'other';

/** The CSS colour of a slot, for SVG fills and strokes and for a `ChartConfig` entry. */
export function seriesColor(slot: SeriesColorSlot): string {
  return slot === 'other' ? 'var(--lm-series-other)' : `var(--lm-series-${slot})`;
}

/** Tailwind background class per slot (lumen-theme.css). Classes are listed whole so Tailwind finds them. */
export const SERIES_BG: Record<SeriesColorSlot, string> = {
  1: 'bg-series-1',
  2: 'bg-series-2',
  3: 'bg-series-3',
  4: 'bg-series-4',
  5: 'bg-series-5',
  6: 'bg-series-6',
  7: 'bg-series-7',
  8: 'bg-series-8',
  other: 'bg-series-other',
};

/** Fixed meaning: the measures `processes` and `goods`, wherever they are drawn together. */
export const MEASURE_SLOT = { processes: 1, goods: 2 } as const satisfies Record<string, SeriesSlot>;

/** Fixed meaning: shipment type as the backend sends it (`Import` / `Export` / `Unknown`). Case-sensitive. */
export function shipmentTypeSlot(type: string): SeriesColorSlot {
  if (type === 'Import') return 1;
  if (type === 'Export') return 2;
  return 'other';
}

export interface Slice {
  /** `slice-1`…, or `others`. Stable, for React keys and ChartConfig keys. */
  key: string;
  name: string;
  value: number;
  slot: SeriesColorSlot;
}

/**
 * Part-to-whole from rows **already sorted** largest first: the first `n` rows in slots 1..n, then one Others slice
 * (slot `other`) with the sum of the rest, only when that sum is above 0. `n` is at most 8.
 */
export function topSlices<T>(
  rows: readonly T[],
  n: number,
  pick: (row: T) => { name: string; value: number },
  othersName: string
): Slice[] {
  const count = Math.min(n, SERIES_SLOTS.length);
  const top = rows.slice(0, count).map((row, index) => ({
    key: `slice-${index + 1}`,
    ...pick(row),
    slot: SERIES_SLOTS[index] as SeriesColorSlot,
  }));
  const othersTotal = rows.slice(count).reduce((sum, row) => sum + pick(row).value, 0);
  return othersTotal > 0 ? [...top, { key: 'others', name: othersName, value: othersTotal, slot: 'other' }] : top;
}

/**
 * Status tones (decision #15). `info` is the `label` blue, `neutral` the muted grey, `muted` quieter still (the
 * control hairline), `err-ink` the darker red of the radar's OVERDUE.
 */
export type StatusTone = 'ok' | 'warn' | 'err' | 'err-ink' | 'info' | 'neutral' | 'muted';

const STATUS_VAR: Record<StatusTone, string> = {
  ok: '--lm-ok',
  warn: '--lm-warn',
  err: '--lm-err',
  'err-ink': '--lm-err-ink',
  info: '--lm-label',
  neutral: '--lm-fg-muted',
  muted: '--lm-line-control',
};

/** The CSS colour of a status tone, for SVG fills and strokes. */
export function statusColor(tone: StatusTone): string {
  return `var(${STATUS_VAR[tone]})`;
}

/** Tailwind background class per status tone. */
export const STATUS_BG: Record<StatusTone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  err: 'bg-err',
  'err-ink': 'bg-err-ink',
  info: 'bg-label',
  neutral: 'bg-fg-muted',
  muted: 'bg-line-control',
};
